/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Table
} from 'react-bootstrap'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import ModalStockAdjustment from '../../components/modal/stockAdjustment/ModalStockAdjustment'
import api from '../../config/AxiosInstance'
import toast, { Toaster } from 'react-hot-toast'
import Loading from '../../components/atoms/Loading'
import { AlertCircle, Info, Menu } from 'lucide-react'
import SelectInput from '../../components/Input/SelectInput'
import { firstLetterUpperCase } from '../../utils/TextFormatter'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#999',
  textTransform: 'uppercase',
  marginBottom: '0.25rem',
  letterSpacing: '0.5px'
}

const valueStyle = {
  fontSize: '0.9rem',
  fontWeight: '500',
  color: '#212529',
  minHeight: '24px'
}

function formatRackType(type) {
  if (!type) return '-'
  const lower = String(type).toLowerCase()
  if (lower === 'double' || lower === 'double deep') return 'Double Deep'
  if (lower === 'rack' || lower === 'selective') return 'Selective'
  if (lower === 'shelving') return 'Shelving'
  if (lower === 'ground' || lower === 'ground floor') return 'Ground Floor'
  return firstLetterUpperCase(type)
}

function CreateReplenishmentPage() {
  const { state } = useLocation()
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [suggestedResult, setSuggestedResult] = useState(null)
  const [totalRequestedPallets, setTotalRequestedPallets] = useState(0)
  const [isOpen, setIsOpen] = useState({
    create: false,
    cancel: false,
    error: false,
    pic: false
  })
  const [errorMessage, setErrorMessage] = useState('')
  const [collapsible, setCollapsible] = useState({})
  const [slotSelected, setSlotSelected] = useState([])
  const [selectPIC, setSelectPIC] = useState(null)
  const [pics, setPics] = useState([])
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState({
    page: false,
    suggest: false,
    submit: false
  })

  const fetchData = async () => {
    try {
      setLoading((prevLoading) => ({ ...prevLoading, page: true }))
      const params = {
        warehouse_id: state?.form_warehouse_id,
        type: state?.type
      }
      const res = await api.get('/replenish/show-rack', { params })
      const { data: dataResponse } = res.data

      const details = (dataResponse || []).map((rack) => ({
        rack_id: rack.rack_id,
        rack: rack.rack,
        rack_type: rack.rack_type,
        slots: (rack.slots || []).map((slot) => ({
          ...slot,
          rack_id: rack.rack_id,
          rack: rack.rack,
          pic: null
        }))
      }))

      setData({
        warehouse_id: state?.form_warehouse_id,
        source: state?.warehouse_name || '',
        type: state?.type,
        details: details
      })
      setCollapsible(
        details.reduce((acc, item) => {
          acc[item.rack_id] = false
          return acc
        }, {})
      )
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message ||
        error?.message ||
        'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
      handleBack()
    } finally {
      setLoading((prevLoading) => ({ ...prevLoading, page: false }))
    }
  }

  const handleCollapseUnassigned = (id) => {
    setCollapsible((prev) => ({
      ...prev,
      [id]: !prev[id]
    }))
  }

  const handleCollapseAssigned = (id) => {
    setCollapsible((prev) => ({
      ...prev,
      ['assigned_' + id]: !prev['assigned_' + id]
    }))
  }

  const handleCollapsePreview = (id) => {
    setCollapsible((prev) => ({
      ...prev,
      ['preview_' + id]: !prev['preview_' + id]
    }))
  }

  const unassignedTasks = React.useMemo(() => {
    if (!data?.details) return []
    return data.details
      .map((detail) => ({
        ...detail,
        task_id: detail.rack_id,
        slots: detail.slots?.filter((slot) => !slot.pic) || []
      }))
      .filter((detail) => detail.slots.length > 0)
  }, [data?.details])

  const assignedTasks = React.useMemo(() => {
    if (!data?.details) return []
    const tasks = []
    data.details.forEach((detail) => {
      const picGroups = {}
      detail.slots?.forEach((slot) => {
        if (slot.pic) {
          if (!picGroups[slot.pic]) picGroups[slot.pic] = []
          picGroups[slot.pic].push(slot)
        }
      })
      Object.keys(picGroups).forEach((pic) => {
        tasks.push({
          ...detail,
          pic,
          slots: picGroups[pic],
          assigned_id: `${detail.rack_id}_${pic}`
        })
      })
    })
    return tasks
  }, [data?.details])

  const [picContext, setPicContext] = useState('unassigned')
  const [assignedSlotSelected, setAssignedSlotSelected] = useState([])

  const handleCheckbox = (type, value = null, taskId = null) => {
    setSlotSelected((prev) => {
      if (type === 'all' && taskId) {
        const rack = unassignedTasks?.find((item) => item?.task_id === taskId)
        if (!rack) return prev

        const exists = prev.find((item) => item.task_id === taskId)

        if (exists && exists.slots?.length === rack.slots?.length) {
          return prev.filter((item) => item.task_id !== taskId)
        }

        const newRack = {
          task_id: taskId,
          rack_id: rack.rack_id,
          rack_name: rack.rack,
          slots: rack.slots || []
        }

        return exists
          ? prev.map((item) =>
              item.task_id === taskId
                ? { ...item, slots: rack.slots || [] }
                : item
            )
          : [...prev, newRack]
      }

      if (value?.task_id) {
        const { task_id, rack_id, palet_id, rack } = value
        const existingRack = prev.find((item) => item.task_id === task_id)

        if (!existingRack) {
          return [
            ...prev,
            {
              task_id,
              rack_id,
              rack_name: rack || '',
              slots: [value]
            }
          ]
        }

        const slotExists = existingRack.slots?.some(
          (slot) => slot.palet_id === palet_id
        )

        return prev
          .map((item) =>
            item.task_id === task_id
              ? {
                  ...item,
                  slots: slotExists
                    ? item.slots.filter((slot) => slot.palet_id !== palet_id)
                    : [...item.slots, value]
                }
              : item
          )
          .filter((item) => item.slots?.length > 0)
      }

      return prev
    })
  }

  const selectedPallets = React.useMemo(() => {
    const currentSelected =
      picContext === 'assigned' ? assignedSlotSelected : slotSelected
    const slots = currentSelected?.reduce((acc, item) => {
      acc.push(...item?.slots)
      return acc
    }, [])?.length
    const slotLabels = currentSelected?.reduce((acc, item) => {
      const label = `${item?.rack_name} (${item?.slots
        ?.map?.((slot) => slot?.palet)
        ?.join(', ')})`
      acc.push(label)
      return acc
    }, [])
    return {
      slots: slots || 0,
      disabledSavePic: selectPIC ? false : true,
      slotLabels: slotLabels?.join(', ')
    }
  }, [slotSelected, assignedSlotSelected, selectPIC, picContext])

  const handleSaveBulkPic = () => {
    const slotSelectedObj = slotSelected
      ?.flatMap((item) => item.slots)
      ?.map((item) => ({
        rack_id: item?.rack_id,
        palet_id: item?.palet_id
      }))
    setData((prev) => ({
      ...prev,
      details: prev?.details?.map((detail) => {
        if (
          slotSelectedObj?.find((item) => item?.rack_id === detail?.rack_id)
        ) {
          return {
            ...detail,
            slots: detail?.slots?.map((slot) => {
              if (
                slotSelectedObj?.find(
                  (item) => item?.palet_id === slot?.palet_id
                )
              ) {
                return {
                  ...slot,
                  pic: selectPIC
                }
              }
              return slot
            })
          }
        }
        return detail
      })
    }))
    setSlotSelected([])
    setSelectPIC(null)
    handleModal()
  }

  const handleCheckboxAssigned = (type, value = null, assignedId = null) => {
    setAssignedSlotSelected((prev) => {
      if (type === 'all' && assignedId) {
        const rack = assignedTasks?.find(
          (item) => item?.assigned_id === assignedId
        )
        if (!rack) return prev

        const exists = prev.find((item) => item.assigned_id === assignedId)

        if (exists && exists.slots?.length === rack.slots?.length) {
          return prev.filter((item) => item.assigned_id !== assignedId)
        }

        const newRack = {
          assigned_id: assignedId,
          rack_name: rack.rack,
          rack_id: rack.rack_id,
          slots: rack.slots || []
        }

        return exists
          ? prev.map((item) =>
              item.assigned_id === assignedId
                ? { ...item, slots: rack.slots || [] }
                : item
            )
          : [...prev, newRack]
      }

      if (value?.assigned_id) {
        const { assigned_id, palet_id } = value
        const existingRack = prev.find(
          (item) => item.assigned_id === assigned_id
        )

        if (!existingRack) {
          return [
            ...prev,
            {
              assigned_id,
              rack_id: value.rack_id,
              rack_name:
                assignedTasks?.find((item) => item?.assigned_id === assigned_id)
                  ?.rack || '',
              slots: [value]
            }
          ]
        }

        const slotExists = existingRack.slots?.some(
          (slot) => slot.palet_id === palet_id
        )

        return prev
          .map((item) =>
            item.assigned_id === assigned_id
              ? {
                  ...item,
                  slots: slotExists
                    ? item.slots.filter((slot) => slot.palet_id !== palet_id)
                    : [...item.slots, value]
                }
              : item
          )
          .filter((item) => item.slots?.length > 0)
      }

      return prev
    })
  }

  const handleSaveBulkPicAssigned = () => {
    const slotSelectedObj = assignedSlotSelected
      ?.flatMap((item) => item.slots)
      ?.map((item) => ({
        rack_id: item?.rack_id,
        palet_id: item?.palet_id
      }))
    setData((prev) => ({
      ...prev,
      details: prev?.details?.map((detail) => {
        if (
          slotSelectedObj?.find((item) => item?.rack_id === detail?.rack_id)
        ) {
          return {
            ...detail,
            slots: detail?.slots?.map((slot) => {
              if (
                slotSelectedObj?.find(
                  (item) => item?.palet_id === slot?.palet_id
                )
              ) {
                return { ...slot, pic: selectPIC }
              }
              return slot
            })
          }
        }
        return detail
      })
    }))
    setAssignedSlotSelected([])
    setSelectPIC(null)
    handleModal()
  }

  const handleSelectAllUnassigned = (e) => {
    if (e.target.checked) {
      setSlotSelected(
        unassignedTasks.map((rack) => ({
          task_id: rack.task_id,
          rack_id: rack.rack_id,
          rack_name: rack.rack,
          slots: rack.slots || []
        }))
      )
    } else {
      setSlotSelected([])
    }
  }

  const handleSelectAllAssigned = (e) => {
    if (e.target.checked) {
      setAssignedSlotSelected(
        assignedTasks.map((rack) => ({
          assigned_id: rack.assigned_id,
          rack_id: rack.rack_id,
          rack_name: rack.rack,
          slots: rack.slots || []
        }))
      )
    } else {
      setAssignedSlotSelected([])
    }
  }

  const handleCancelAssigned = () => {
    const slotSelectedObj = assignedSlotSelected
      ?.flatMap((item) => item.slots)
      ?.map((item) => ({
        rack_id: item?.rack_id,
        palet_id: item?.palet_id
      }))

    setData((prev) => ({
      ...prev,
      details: prev?.details?.map((detail) => {
        if (
          slotSelectedObj?.find((item) => item?.rack_id === detail?.rack_id)
        ) {
          return {
            ...detail,
            slots: detail?.slots?.map((slot) => {
              if (
                slotSelectedObj?.find(
                  (item) => item?.palet_id === slot?.palet_id
                )
              ) {
                return { ...slot, pic: null }
              }
              return slot
            })
          }
        }
        return detail
      })
    }))
    setAssignedSlotSelected([])
  }

  const handleChangePicAssignedBulk = (assignedId, newPic) => {
    const task = assignedTasks.find((t) => t.assigned_id === assignedId)
    if (!task) return
    const paletIds = task.slots.map((s) => s.palet_id)

    setData((prev) => ({
      ...prev,
      details: prev?.details?.map((detail) => {
        if (detail?.rack_id === task.rack_id) {
          return {
            ...detail,
            slots: detail?.slots?.map((slot) => {
              if (paletIds.includes(slot?.palet_id)) {
                return { ...slot, pic: newPic }
              }
              return slot
            })
          }
        }
        return detail
      })
    }))
  }

  const columnRacksUnassigned = [
    {
      key: 'checkbox',
      label: '',
      component: (value) => (
        <div className="d-flex justify-content-center">
          <input
            type="checkbox"
            role="button"
            className="staging-area form-check-input"
            onChange={() => handleCheckbox('all', null, value.task_id)}
            style={{ cursor: 'pointer' }}
            checked={
              slotSelected?.find((slot) => slot?.task_id === value.task_id) &&
              slotSelected?.find((slot) => slot.task_id === value.task_id)
                ?.slots?.length === value.slots?.length
                ? true
                : false
            }
          />
        </div>
      )
    },
    { key: 'rack', label: 'Rack Name' },
    {
      key: 'rack_type',
      label: 'Type',
      component: (value) => formatRackType(value.rack_type)
    },
    {
      key: 'action',
      label: 'Action',
      className: 'text-end',
      component: (value) => (
        <div key={value?.rack_id} className="d-flex justify-content-end">
          <Button
            variant="transparent"
            className="btn-secondary"
            size="md"
            onClick={() => handleCollapseUnassigned(value.task_id)}
          >
            <Menu size={16} />
          </Button>
        </div>
      )
    }
  ]

  const columnSlotsUnassigned = [
    {
      key: 'checkbox',
      label: '',
      className: 'text-center',
      component: (value) => (
        <div className="d-flex justify-content-center">
          <input
            type="checkbox"
            role="button"
            className="staging-area form-check-input"
            style={{ cursor: 'pointer' }}
            onChange={() => handleCheckbox('slot', value)}
            checked={
              slotSelected
                ?.find((item) => item.task_id === value.task_id)
                ?.slots?.some((slot) => slot.palet_id === value.palet_id) ||
              false
            }
          />
        </div>
      )
    },
    {
      key: 'palet',
      label: 'Pallet',
      component: (value) => value?.palet || '-'
    },
    {
      key: 'batch',
      label: 'Batch',
      component: (value) => value?.batch || '-'
    },
    {
      key: 'item_info',
      label: 'Item Code - Name',
      component: (value) =>
        value?.item_code || value?.item_name
          ? `${value?.item_code || ''} - ${value?.item_name || ''}`
          : '-'
    },
    {
      key: 'from',
      label: 'Source',
      component: (value) => value?.source_code || value?.from || '-'
    }
  ]

  const columnRacksAssigned = [
    {
      key: 'checkbox',
      label: '',
      component: (value) => (
        <div className="d-flex justify-content-center">
          <input
            type="checkbox"
            role="button"
            className="staging-area form-check-input"
            onChange={() =>
              handleCheckboxAssigned('all', null, value.assigned_id)
            }
            style={{ cursor: 'pointer' }}
            checked={
              assignedSlotSelected?.find(
                (slot) => slot?.assigned_id === value.assigned_id
              ) &&
              assignedSlotSelected?.find(
                (slot) => slot.assigned_id === value.assigned_id
              )?.slots?.length === value.slots?.length
                ? true
                : false
            }
          />
        </div>
      )
    },
    { key: 'rack', label: 'Rack Name' },
    {
      key: 'rack_type',
      label: 'Type',
      component: (value) => formatRackType(value.rack_type)
    },
    {
      key: 'pic',
      label: 'PIC',
      component: (value) => (
        <div style={{ width: '150px' }}>
          <SelectInput
            id={`${value.assigned_id}-pic`}
            handleChange={(e) =>
              handleChangePicAssignedBulk(value.assigned_id, e.target.value)
            }
            options={pics || []}
            value={value.pic || ''}
          />
        </div>
      )
    },
    {
      key: 'action',
      label: 'Action',
      className: 'text-end',
      component: (value) => (
        <div key={value?.assigned_id} className="d-flex justify-content-end">
          <Button
            variant="transparent"
            className="btn-secondary"
            size="md"
            onClick={() => handleCollapseAssigned(value.assigned_id)}
          >
            <Menu size={16} />
          </Button>
        </div>
      )
    }
  ]

  const columnSlotsAssigned = [
    {
      key: 'checkbox',
      label: '',
      className: 'text-center',
      component: (value) => (
        <div className="d-flex justify-content-center">
          <input
            type="checkbox"
            role="button"
            className="staging-area form-check-input"
            style={{ cursor: 'pointer' }}
            onChange={() => handleCheckboxAssigned('slot', value)}
            checked={
              assignedSlotSelected
                ?.find((item) => item.assigned_id === value.assigned_id)
                ?.slots?.some((slot) => slot.palet_id === value.palet_id) ||
              false
            }
          />
        </div>
      )
    },
    {
      key: 'palet',
      label: 'Pallet',
      component: (value) => value?.palet || '-'
    },
    {
      key: 'batch',
      label: 'Batch',
      component: (value) => value?.batch || '-'
    },
    {
      key: 'item_info',
      label: 'Item Code - Name',
      component: (value) =>
        value?.item_code || value?.item_name
          ? `${value?.item_code || ''} - ${value?.item_name || ''}`
          : '-'
    },
    {
      key: 'from',
      label: 'Source',
      component: (value) => value?.source_code || value?.from || '-'
    }
  ]

  const columnRacksPreview = [
    { key: 'rack', label: 'Rack Name' },
    {
      key: 'type',
      label: 'Type',
      component: (value) => formatRackType(value.type || value.rack_type)
    },
    {
      key: 'pic',
      label: 'PIC',
      component: (value) => value.pic || '-'
    },
    {
      key: 'action',
      label: 'Action',
      className: 'text-end',
      component: (value) => (
        <div key={value?.rack_id} className="d-flex justify-content-end">
          <Button
            variant="transparent"
            className="btn-secondary"
            size="md"
            onClick={() => handleCollapsePreview(value.rack_id)}
          >
            <Menu size={16} />
          </Button>
        </div>
      )
    }
  ]

  const columnSlotsPreview = [
    {
      key: 'palet',
      label: 'Pallet',
      component: (value) => value?.pallet || value?.palet || '-'
    },
    {
      key: 'batch',
      label: 'Batch',
      component: (value) => value?.batch || '-'
    },
    {
      key: 'item_info',
      label: 'Item Code - Name',
      component: (value) =>
        value?.item_code || value?.item_name
          ? `${value?.item_code || ''} - ${value?.item_name || ''}`
          : '-'
    },
    {
      key: 'source',
      label: 'Source',
      component: (value) => value?.source || value?.source_code || '-'
    },
    {
      key: 'destination',
      label: 'Destination',
      component: (value) => value?.destination || '-'
    }
  ]

  const handleBack = (isSubmit = false) => {
    navigate('/replenishment', {
      replace: true,
      state: !isSubmit
        ? {
            ...state,
            isCancel: true
          }
        : null
    })
  }

  const handleModal = (type = null, context = 'unassigned') => {
    if (type === 'pic') setPicContext(context)
    if (!type) {
      const trueKey = Object.keys(isOpen).find((key) => isOpen[key])
      const isOpenValue = !isOpen[trueKey]
      if (trueKey) {
        setIsOpen((prev) => ({ ...prev, [trueKey]: isOpenValue }))
      }
      if (trueKey === 'pic' && !isOpenValue) {
        setSelectPIC(null)
      }
    } else {
      setIsOpen((prev) => ({
        ...prev,
        [type]: !prev[type]
      }))
    }
  }

  const fetchPic = async () => {
    try {
      const params = {
        warehouse_id: state?.form_warehouse_id || ''
      }
      const res = await api.get('/replenish/users', { params })
      const { data } = res
      if (data.success) {
        setPics(data?.data?.map(({ id, name }) => ({ value: id, label: name })))
      }
    } catch (error) {
      const err =
        error?.response?.data?.errors?.message ||
        error?.message ||
        'An error occurred'
      toast.error(err, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    }
  }

  const ModalProp = useCallback(() => {
    if (isOpen.create) {
      return {
        headerTitle: '',
        title: 'Create Ticket',
        key: 'create',
        description:
          'You will have to finish this ticket before creating another ticket! continue creating?',
        footer: (
          <div className="d-flex gap-2">
            <Button
              variant="transparent"
              disabled={loading.submit}
              onClick={() => handleModal()}
            >
              Cancel
            </Button>
            <Button
              variant="transparent"
              className={`${
                loading.submit
                  ? 'btn-secondary opacity-25'
                  : 'btn-primary-orange'
              }`}
              disabled={loading.submit}
              onClick={() => onSubmit(true)}
            >
              {loading.submit ? 'Loading...' : 'Create'}
            </Button>
          </div>
        )
      }
    }
    if (isOpen.cancel) {
      return {
        headerTitle: '',
        key: 'cancel',
        title: 'Cancel creating ticket?',
        description: '',
        footer: (
          <div className="d-flex gap-2">
            <Button variant="transparent" onClick={() => handleModal()}>
              Continue
            </Button>
            <Button
              variant="transparent"
              className="btn-primary-orange"
              onClick={() => handleBack()}
            >
              Cancel
            </Button>
          </div>
        )
      }
    }
    if (isOpen.error) {
      return {
        headerTitle: 'Replenishment Error',
        key: 'error',
        title: 'Action failed!',
        description:
          errorMessage ||
          'Failed to process replenishment. Please check your selected tasks and cycle count status.',
        footer: (
          <div className="d-flex gap-2">
            <Button
              variant="transparent"
              className="btn-primary-orange"
              onClick={() => handleModal()}
            >
              OK
            </Button>
          </div>
        )
      }
    }
    if (isOpen.pic) {
      return {
        headerTitle: 'Select Checker and PIC',
        title: '',
        key: 'pic',
        description: '',
        footer: (
          <div className="d-flex gap-2">
            <Button
              variant="transparent"
              className={`${
                selectedPallets?.disabledSavePic
                  ? 'btn-secondary opacity-25'
                  : 'btn-primary-orange'
              }`}
              onClick={() =>
                picContext === 'assigned'
                  ? handleSaveBulkPicAssigned()
                  : handleSaveBulkPic()
              }
              disabled={selectedPallets?.disabledSavePic}
            >
              SAVE
            </Button>
          </div>
        )
      }
    }
    return {
      headerTitle: '',
      key: 'null',
      title: '',
      description: '',
      footer: null
    }
  }, [isOpen, loading.submit, selectPIC])

  const handleContinue = async () => {
    try {
      if (assignedTasks.length === 0) {
        toast.error('Please assign PIC to at least one pallet', {
          position: 'top-right'
        })
        return
      }

      setLoading((prev) => ({ ...prev, suggest: true }))

      const selectedPaletIds =
        assignedSlotSelected.length > 0
          ? new Set(
              assignedSlotSelected.flatMap((item) =>
                item.slots.map((s) => s.palet_id)
              )
            )
          : null

      const rackMap = new Map()
      data?.details?.forEach((detail) => {
        const assignedSlots = (detail.slots || []).filter((s) => {
          if (!s.pic) return false
          if (selectedPaletIds) return selectedPaletIds.has(s.palet_id)
          return true
        })
        assignedSlots.forEach((slot) => {
          const key = `${detail.rack_id}_${slot.pic}`
          if (!rackMap.has(key)) {
            rackMap.set(key, {
              rack_id: detail.rack_id,
              pic: slot.pic,
              slots: []
            })
          }
          rackMap.get(key).slots.push({
            slot_id: slot.slot_source_id,
            pallet_id: slot.palet_id
          })
        })
      })

      const suggestRacksPayload = Array.from(rackMap.values())

      const requestedPalletCount = suggestRacksPayload.reduce(
        (acc, r) => acc + (r.slots?.length || 0),
        0
      )
      setTotalRequestedPallets(requestedPalletCount)

      const suggestRes = await api.post('/replenish/suggest-rack', {
        type: state?.type,
        racks: suggestRacksPayload
      })

      const suggestedData = suggestRes?.data?.data

      if (!suggestedData?.racks || suggestedData.racks.length === 0) {
        throw new Error(
          'No available destination slots found for the selected pallets'
        )
      }

      setSuggestedResult(suggestedData)
      setCollapsible(
        (suggestedData.racks || []).reduce((acc, r) => {
          acc['preview_' + r.rack_id] = true
          return acc
        }, {})
      )
      setStep(2)
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'An error occurred'
      setErrorMessage(errMsg)
      handleModal('error')
    } finally {
      setLoading((prev) => ({ ...prev, suggest: false }))
    }
  }

  const onSubmit = async (isConfirm = false) => {
    try {
      if (!state?.form_warehouse_id) {
        toast.error('Please select a warehouse', {
          position: 'top-right'
        })
        return
      }

      if (!suggestedResult?.racks || suggestedResult.racks.length === 0) {
        toast.error('No suggested tasks to submit', {
          position: 'top-right'
        })
        return
      }

      setLoading((prevLoading) => ({ ...prevLoading, submit: true }))

      const createPayload = {
        warehouse_id: state?.form_warehouse_id,
        type: state?.type,
        racks: suggestedResult.racks.map((r) => ({
          rack_id: r.rack_id,
          pic_id: r.pic_id,
          slots: r.slots.map((s) => ({
            source_id: s.source_id,
            destination_id: s.destination_id,
            pallet_id: s.pallet_id
          }))
        }))
      }

      const res = await api.post('/replenish', createPayload)
      const { data: dataResponse } = res.data
      if (dataResponse) {
        toast.success('Ticket Created!', {
          position: 'top-right',
          style: {
            fontSize: '14px',
            fontWeight: '600'
          }
        })
        handleBack(true)
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'An error occurred'
      setErrorMessage(errMsg)
      handleModal()
      setTimeout(() => {
        handleModal('error')
      }, 300)
    } finally {
      setIsOpen((prev) => ({ ...prev, create: false }))
      setLoading((prevLoading) => ({ ...prevLoading, submit: false }))
    }
  }

  useEffect(() => {
    if (state?.form_warehouse_id && pics.length === 0) {
      fetchPic()
    }
    fetchData()
  }, [state])

  if (loading.page) {
    return <Loading />
  }

  const renderInfoItem = (label, value, col = 3) => (
    <Form.Group className={`mb-3 col-12 col-md-${col}`}>
      <Form.Label style={labelStyle}>{label}</Form.Label>
      <div className="px-0" style={valueStyle}>
        {value || '-'}
      </div>
    </Form.Group>
  )

  return (
    <>
      <Toaster />
      <Container fluid className="px-4 mb-5">
        <Row className="mb-4">
          <Col md={12}>
            <div className="d-flex flex-column justify-content-start">
              <h4
                style={headerTitleStyle}
                className="d-flex align-items-center gap-2 mb-1"
              >
                Replenishment
              </h4>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb fs-6 mb-0">
                  <li className="breadcrumb-item">
                    <Link
                      to="/replenishment"
                      state={{
                        q: state?.q,
                        warehouse_id: state?.warehouse_id,
                        status: state?.status
                      }}
                      className="text-decoration-none"
                    >
                      <span className="text-black fw-bold text-uppercase">
                        REPLENISHMENT
                      </span>
                    </Link>
                  </li>
                  <li
                    className="breadcrumb-item active"
                    aria-current="page"
                    style={{ color: '#6c757d' }}
                  >
                    CREATE
                  </li>
                </ol>
              </nav>
            </div>
          </Col>
        </Row>

        <Card style={cardFilterStyle}>
          <Row>
            {renderInfoItem('WAREHOUSE', data?.source, 6)}
            {renderInfoItem(
              'TYPE',
              data?.type ? firstLetterUpperCase(data?.type) : '',
              6
            )}
          </Row>
        </Card>

        {step === 1 && (
          <>
            <Card style={cardFilterStyle}>
              <Row>
                <Col md={12}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5
                      className="fw-bold text-uppercase mb-0"
                      style={{ fontSize: '0.85rem', color: '#6c757d' }}
                    >
                      UNASSIGNED TASK
                    </h5>
                    <Button
                      variant="dark"
                      size="sm"
                      style={{
                        backgroundColor: '#202A44',
                        border: 'none',
                        padding: '0.4rem 1rem'
                      }}
                      onClick={() => handleModal('pic', 'unassigned')}
                      disabled={slotSelected?.length === 0}
                    >
                      Select PIC
                    </Button>
                  </div>

                  <Table
                    hover
                    responsive
                    className="bg-white shadow-sm"
                    style={{
                      fontSize: '13px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      marginBottom: 0,
                      border: '1px solid #e9ecef'
                    }}
                  >
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        {columnRacksUnassigned.map((column) => (
                          <th
                            className={`align-middle p-3 ${column.className || ''}`}
                            key={column.key}
                            style={{ ...tableHeaderStyle, borderTop: 'none' }}
                          >
                            {column.key === 'checkbox' ? (
                              <div className="d-flex justify-content-center">
                                <input
                                  type="checkbox"
                                  className="form-check-input staging-area"
                                  role="button"
                                  style={{ cursor: 'pointer' }}
                                  onChange={handleSelectAllUnassigned}
                                  checked={
                                    unassignedTasks?.length > 0 &&
                                    slotSelected?.length ===
                                      unassignedTasks?.length &&
                                    unassignedTasks.every(
                                      (rack) =>
                                        slotSelected.find(
                                          (s) => s.task_id === rack.task_id
                                        )?.slots?.length === rack.slots?.length
                                    )
                                  }
                                />
                              </div>
                            ) : (
                              <span>{column.label}</span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {unassignedTasks?.length === 0 && (
                        <tr>
                          <td
                            colSpan={columnRacksUnassigned.length}
                            className="p-4 text-center text-muted"
                          >
                            No unassigned task
                          </td>
                        </tr>
                      )}
                      {unassignedTasks?.length > 0 &&
                        unassignedTasks.map((item, index) => {
                          return (
                            <React.Fragment key={`${item.rack_id}-${index}`}>
                              <tr>
                                {columnRacksUnassigned.map((column) => (
                                  <td
                                    key={column.key}
                                    className={`align-middle p-3 ${column.className || ''}`}
                                    style={{
                                      borderBottom: '1px solid #e9ecef'
                                    }}
                                  >
                                    {column.component
                                      ? column.component(item)
                                      : item[column.key]}
                                  </td>
                                ))}
                              </tr>
                              {collapsible[item.task_id] && (
                                <tr>
                                  <td
                                    colSpan={columnRacksUnassigned.length}
                                    className="p-0"
                                    style={{
                                      borderBottom: '1px solid #e9ecef'
                                    }}
                                  >
                                    <div className="p-0 bg-white">
                                      <Table responsive className="mb-0">
                                        <thead
                                          style={{ backgroundColor: '#fdfdfd' }}
                                        >
                                          <tr>
                                            {columnSlotsUnassigned.map(
                                              (column) => (
                                                <th
                                                  className={`align-middle px-4 py-2 ${column.className || ''}`}
                                                  key={column.key}
                                                  style={{
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    color: '#6c757d',
                                                    borderBottom:
                                                      '1px solid #e9ecef',
                                                    borderTop: 'none'
                                                  }}
                                                >
                                                  <span>{column.label}</span>
                                                </th>
                                              )
                                            )}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {item.slots?.length === 0 && (
                                            <tr>
                                              <td
                                                colSpan={
                                                  columnSlotsUnassigned.length
                                                }
                                                className="px-4 py-3 text-center"
                                                style={{ borderBottom: 'none' }}
                                              >
                                                No data
                                              </td>
                                            </tr>
                                          )}
                                          {item.slots?.length > 0 &&
                                            item.slots.map(
                                              (slotItem, slotIndex) => {
                                                const slotWithTask = {
                                                  ...slotItem,
                                                  task_id: item.task_id,
                                                  rack: item.rack
                                                }
                                                return (
                                                  <tr
                                                    key={`${slotItem.palet_id}-${slotIndex}`}
                                                  >
                                                    {columnSlotsUnassigned.map(
                                                      (column) => (
                                                        <td
                                                          key={column.key}
                                                          className={`align-middle px-4 py-3 ${column.className || ''}`}
                                                          style={{
                                                            fontSize: '13px',
                                                            borderBottom:
                                                              slotIndex ===
                                                              item.slots
                                                                .length -
                                                                1
                                                                ? 'none'
                                                                : '1px solid #e9ecef'
                                                          }}
                                                        >
                                                          {column.component
                                                            ? column.component(
                                                                slotWithTask
                                                              )
                                                            : slotItem[
                                                                column.key
                                                              ]}
                                                        </td>
                                                      )
                                                    )}
                                                  </tr>
                                                )
                                              }
                                            )}
                                        </tbody>
                                      </Table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          )
                        })}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </Card>

            <Card style={cardFilterStyle}>
              <Row>
                <Col md={12}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5
                      className="fw-bold text-uppercase mb-0"
                      style={{ fontSize: '0.85rem', color: '#6c757d' }}
                    >
                      ASSIGNED TASK
                    </h5>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleCancelAssigned}
                        disabled={assignedSlotSelected?.length === 0}
                      >
                        Cancel Task
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        style={{
                          backgroundColor: '#6c757d',
                          border: 'none',
                          color: '#fff'
                        }}
                        onClick={() => handleModal('pic', 'assigned')}
                        disabled={assignedSlotSelected?.length === 0}
                      >
                        Change PIC
                      </Button>
                    </div>
                  </div>

                  <Table
                    responsive
                    className="bg-white shadow-sm"
                    style={{
                      fontSize: '13px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      marginBottom: 0,
                      border: '1px solid #e9ecef'
                    }}
                  >
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        {columnRacksAssigned.map((column) => (
                          <th
                            className={`align-middle p-3 ${column.className || ''}`}
                            key={column.key}
                            style={{ ...tableHeaderStyle, borderTop: 'none' }}
                          >
                            {column.key === 'checkbox' ? (
                              <div className="d-flex justify-content-center">
                                <input
                                  type="checkbox"
                                  className="form-check-input staging-area"
                                  role="button"
                                  style={{ cursor: 'pointer' }}
                                  onChange={handleSelectAllAssigned}
                                  checked={
                                    assignedTasks?.length > 0 &&
                                    assignedSlotSelected?.length ===
                                      assignedTasks?.length &&
                                    assignedTasks.every(
                                      (rack) =>
                                        assignedSlotSelected.find(
                                          (s) =>
                                            s.assigned_id === rack.assigned_id
                                        )?.slots?.length === rack.slots?.length
                                    )
                                  }
                                />
                              </div>
                            ) : (
                              <span>{column.label}</span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {assignedTasks?.length === 0 && (
                        <tr>
                          <td
                            colSpan={columnRacksAssigned.length}
                            className="p-4 text-center text-muted"
                          >
                            No assigned task
                          </td>
                        </tr>
                      )}
                      {assignedTasks?.length > 0 &&
                        assignedTasks.map((item, index) => {
                          return (
                            <React.Fragment
                              key={`${item.assigned_id}-${index}`}
                            >
                              <tr>
                                {columnRacksAssigned.map((column) => (
                                  <td
                                    key={column.key}
                                    className={`align-middle p-3 ${column.className || ''}`}
                                    style={{
                                      borderBottom: '1px solid #e9ecef'
                                    }}
                                  >
                                    {column.component
                                      ? column.component(item)
                                      : item[column.key]}
                                  </td>
                                ))}
                              </tr>
                              {collapsible['assigned_' + item.assigned_id] && (
                                <tr>
                                  <td
                                    colSpan={columnRacksAssigned.length}
                                    className="p-0"
                                    style={{
                                      borderBottom: '1px solid #e9ecef'
                                    }}
                                  >
                                    <div className="p-0 bg-white">
                                      <Table responsive className="mb-0">
                                        <thead
                                          style={{ backgroundColor: '#fdfdfd' }}
                                        >
                                          <tr>
                                            {columnSlotsAssigned.map(
                                              (column) => (
                                                <th
                                                  className={`align-middle px-4 py-2 ${column.className || ''}`}
                                                  key={column.key}
                                                  style={{
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    color: '#6c757d',
                                                    borderBottom:
                                                      '1px solid #e9ecef',
                                                    borderTop: 'none'
                                                  }}
                                                >
                                                  <span>{column.label}</span>
                                                </th>
                                              )
                                            )}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {item.slots?.length === 0 && (
                                            <tr>
                                              <td
                                                colSpan={
                                                  columnSlotsAssigned.length
                                                }
                                                className="px-4 py-3 text-center"
                                                style={{ borderBottom: 'none' }}
                                              >
                                                No data
                                              </td>
                                            </tr>
                                          )}
                                          {item.slots?.length > 0 &&
                                            item.slots.map(
                                              (slotItem, slotIndex) => {
                                                const slotWithAssigned = {
                                                  ...slotItem,
                                                  assigned_id: item.assigned_id
                                                }
                                                return (
                                                  <tr
                                                    key={`${slotItem.palet_id}-${slotIndex}`}
                                                  >
                                                    {columnSlotsAssigned.map(
                                                      (column) => (
                                                        <td
                                                          key={column.key}
                                                          className={`align-middle px-4 py-3 ${column.className || ''}`}
                                                          style={{
                                                            fontSize: '13px',
                                                            borderBottom:
                                                              slotIndex ===
                                                              item.slots
                                                                .length -
                                                                1
                                                                ? 'none'
                                                                : '1px solid #e9ecef'
                                                          }}
                                                        >
                                                          {column.component
                                                            ? column.component(
                                                                slotWithAssigned
                                                              )
                                                            : slotItem[
                                                                column.key
                                                              ]}
                                                        </td>
                                                      )
                                                    )}
                                                  </tr>
                                                )
                                              }
                                            )}
                                        </tbody>
                                      </Table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          )
                        })}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </Card>

            <div className="d-flex justify-content-between align-items-center mt-4">
              <Button
                variant="outline-danger"
                className="px-4 py-2"
                onClick={() => handleModal('cancel')}
                style={{ borderRadius: '6px', fontSize: '0.85rem' }}
              >
                Cancel
              </Button>
              <Button
                variant="secondary"
                className="px-4 py-2"
                style={{
                  backgroundColor:
                    assignedTasks?.length > 0 ? '#f89d1d' : '#ccc',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.85rem'
                }}
                disabled={assignedTasks?.length === 0 || loading.suggest}
                onClick={handleContinue}
              >
                {loading.suggest ? 'Processing...' : 'Continue'}
              </Button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            {(() => {
              const totalAssignedPallets = totalRequestedPallets
              const totalSuggestedPallets = (
                suggestedResult?.racks || []
              ).reduce((acc, rack) => acc + (rack.slots?.length || 0), 0)
              const unassignedCount =
                totalAssignedPallets - totalSuggestedPallets

              if (unassignedCount > 0) {
                return (
                  <Alert
                    variant="warning"
                    className="d-flex align-items-start gap-3 mb-4 p-3 shadow-sm border-0"
                    style={{
                      backgroundColor: '#fff8e6',
                      color: '#8a5b00',
                      borderRadius: '10px'
                    }}
                  >
                    <AlertCircle
                      size={22}
                      className="flex-shrink-0 text-warning mt-1"
                    />
                    <div style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>
                      <div
                        className="fw-bold mb-1"
                        style={{ fontSize: '0.95rem' }}
                      >
                        Pemberitahuan Penempatan Slot Tujuan
                      </div>
                      Hanya <strong>{totalSuggestedPallets}</strong> dari{' '}
                      <strong>{totalAssignedPallets}</strong> pallet yang
                      mendapatkan slot tujuan. Sebanyak{' '}
                      <strong>{unassignedCount} pallet</strong> lainnya belum
                      memiliki slot tujuan yang kosong dan sesuai
                      kriteria/kategori di rak tersebut.
                    </div>
                  </Alert>
                )
              }

              return (
                <Alert
                  variant="success"
                  className="d-flex align-items-center gap-3 mb-4 p-3 shadow-sm border-0"
                  style={{
                    backgroundColor: '#eefbf3',
                    color: '#156a38',
                    borderRadius: '10px'
                  }}
                >
                  <Info size={20} className="flex-shrink-0 text-success" />
                  <div style={{ fontSize: '0.875rem' }}>
                    Seluruh <strong>{totalSuggestedPallets} pallet</strong> yang
                    dipilih berhasil mendapatkan slot tujuan.
                  </div>
                </Alert>
              )
            })()}

            <Card style={cardFilterStyle}>
              <Row>
                <Col md={12}>
                  <h5
                    className="fw-bold text-uppercase mb-3"
                    style={{ fontSize: '0.85rem', color: '#6c757d' }}
                  >
                    LIST TASK
                  </h5>

                  <Table
                    responsive
                    className="bg-white shadow-sm"
                    style={{
                      fontSize: '13px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      marginBottom: 0,
                      border: '1px solid #e9ecef'
                    }}
                  >
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        {columnRacksPreview.map((column) => (
                          <th
                            className={`align-middle p-3 ${column.className || ''}`}
                            key={column.key}
                            style={{ ...tableHeaderStyle, borderTop: 'none' }}
                          >
                            <span>{column.label}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {suggestedResult?.racks?.length === 0 && (
                        <tr>
                          <td
                            colSpan={columnRacksPreview.length}
                            className="p-4 text-center text-muted"
                          >
                            No list task available
                          </td>
                        </tr>
                      )}
                      {suggestedResult?.racks?.length > 0 &&
                        suggestedResult.racks.map((item, index) => {
                          return (
                            <React.Fragment key={`${item.rack_id}-${index}`}>
                              <tr>
                                {columnRacksPreview.map((column) => (
                                  <td
                                    key={column.key}
                                    className={`align-middle p-3 ${column.className || ''}`}
                                    style={{
                                      borderBottom: '1px solid #e9ecef'
                                    }}
                                  >
                                    {column.component
                                      ? column.component(item)
                                      : item[column.key]}
                                  </td>
                                ))}
                              </tr>
                              {collapsible['preview_' + item.rack_id] && (
                                <tr>
                                  <td
                                    colSpan={columnRacksPreview.length}
                                    className="p-0"
                                    style={{
                                      borderBottom: '1px solid #e9ecef'
                                    }}
                                  >
                                    <div className="p-0 bg-white">
                                      <Table responsive className="mb-0">
                                        <thead
                                          style={{ backgroundColor: '#fdfdfd' }}
                                        >
                                          <tr>
                                            {columnSlotsPreview.map(
                                              (column) => (
                                                <th
                                                  className={`align-middle px-4 py-2 ${column.className || ''}`}
                                                  key={column.key}
                                                  style={{
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    color: '#6c757d',
                                                    borderBottom:
                                                      '1px solid #e9ecef',
                                                    borderTop: 'none'
                                                  }}
                                                >
                                                  <span>{column.label}</span>
                                                </th>
                                              )
                                            )}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {item.slots?.length === 0 && (
                                            <tr>
                                              <td
                                                colSpan={
                                                  columnSlotsPreview.length
                                                }
                                                className="px-4 py-3 text-center"
                                                style={{ borderBottom: 'none' }}
                                              >
                                                No data
                                              </td>
                                            </tr>
                                          )}
                                          {item.slots?.length > 0 &&
                                            item.slots.map(
                                              (slotItem, slotIndex) => {
                                                return (
                                                  <tr
                                                    key={`${slotItem.pallet_id}-${slotIndex}`}
                                                  >
                                                    {columnSlotsPreview.map(
                                                      (column) => (
                                                        <td
                                                          key={column.key}
                                                          className={`align-middle px-4 py-3 ${column.className || ''}`}
                                                          style={{
                                                            fontSize: '13px',
                                                            borderBottom:
                                                              slotIndex ===
                                                              item.slots
                                                                .length -
                                                                1
                                                                ? 'none'
                                                                : '1px solid #e9ecef'
                                                          }}
                                                        >
                                                          {column.component
                                                            ? column.component(
                                                                slotItem
                                                              )
                                                            : slotItem[
                                                                column.key
                                                              ]}
                                                        </td>
                                                      )
                                                    )}
                                                  </tr>
                                                )
                                              }
                                            )}
                                        </tbody>
                                      </Table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          )
                        })}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </Card>

            <div className="d-flex justify-content-between align-items-center mt-4">
              <Button
                variant="outline-danger"
                className="px-4 py-2"
                onClick={() => setStep(1)}
                style={{ borderRadius: '6px', fontSize: '0.85rem' }}
              >
                Back
              </Button>
              <Button
                className="btn-primary-orange px-4 py-2"
                style={{
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.85rem'
                }}
                disabled={loading.submit}
                onClick={() => handleModal('create')}
              >
                {loading.submit ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </>
        )}

        <ModalStockAdjustment
          show={isOpen[ModalProp()?.key]}
          onHide={handleModal}
          title={ModalProp()?.headerTitle}
          size="md"
          backdrop={true}
        >
          <React.Fragment>
            {ModalProp()?.key !== 'pic' ? (
              <div className="p-2 mt-3 text-center">
                <div className="d-flex justify-content-center">
                  <svg
                    width="181"
                    height="180"
                    viewBox="0 0 181 180"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M90.5 0C40.7991 0 0.5 40.2991 0.5 90C0.5 139.701 40.7991 180 90.5 180C140.201 180 180.5 139.701 180.5 90C180.5 40.2991 140.201 0 90.5 0ZM90.5 164.732C49.2366 164.732 15.7679 131.263 15.7679 90C15.7679 48.7366 49.2366 15.2679 90.5 15.2679C131.763 15.2679 165.232 48.7366 165.232 90C165.232 131.263 131.763 164.732 90.5 164.732Z"
                      fill="#828282"
                    />
                    <path
                      d="M80.8574 125.357C80.8574 127.915 81.8734 130.367 83.6817 132.176C85.4901 133.984 87.9428 135 90.5003 135C93.0577 135 95.5104 133.984 97.3188 132.176C99.1272 130.367 100.143 127.915 100.143 125.357C100.143 122.8 99.1272 120.347 97.3188 118.539C95.5104 116.73 93.0577 115.714 90.5003 115.714C87.9428 115.714 85.4901 116.73 83.6817 118.539C81.8734 120.347 80.8574 122.8 80.8574 125.357ZM85.6789 102.857H95.3217C96.2056 102.857 96.9289 102.134 96.9289 101.25V46.6071C96.9289 45.7232 96.2056 45 95.3217 45H85.6789C84.7949 45 84.0717 45.7232 84.0717 46.6071V101.25C84.0717 102.134 84.7949 102.857 85.6789 102.857Z"
                      fill="#828282"
                    />
                  </svg>
                </div>
                <div className="my-3">
                  <p>{ModalProp().title}</p>
                  {ModalProp().description !== '' && (
                    <p className="fw-bold">{ModalProp().description}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="my-2 p-2 ms-2">
                <p>
                  <span className="text-muted mr-2">for </span>
                  <span className="fw-bold">
                    {selectedPallets?.slotLabels || ''}
                  </span>
                </p>
                <SelectInput
                  id="modal-pic"
                  handleChange={(e) => setSelectPIC(e.target.value)}
                  label={'PIC'}
                  options={pics || []}
                  value={selectPIC || ''}
                />
              </div>
            )}
          </React.Fragment>
          <Modal.Footer>
            <hr />
            {ModalProp().footer}
          </Modal.Footer>
        </ModalStockAdjustment>
      </Container>
    </>
  )
}

export default CreateReplenishmentPage
